import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EnumCommodity, EnumTipoSujeto } from '../../../../api'
import { AnnexedParamsModel } from '../../../../api/Argentia/endpoints/annexedParams/model/annexedParams'
import { ProductSelectOption } from '../../../../api/Argentia/endpoints/contracts/model/contracts'
import { ProfileModel } from '../../../../api/Argentia/endpoints/profiles/model/profiles'
import { EnumEstadoLead } from '../../../../api/oData/enums/enumEstadoLead'
import { EnumNivelDesglose } from '../../../../api/Argentia/enums/enumNivelDesglose'
import { EnumProrrateo } from '../../../../api/Argentia/enums/enumProrrateo'
import { EnumTipoLead } from '../../../../api/oData/enums/enumTipoLead'
import { EnumTipoParamAnexo } from '../../../../api/oData/enums/enumTipoParamAnexo'
import { AnnexedParamAccordionSection } from '../../../../components/shared/annexedParamAccordionSection/annexedParamAccordionSection'
import { mapAnnexedParamFormDataToAnnexedParamTable } from '../../../../components/shared/annexedParamAccordionSection/annexedParamAccordionSection.mapper'
import {
  AnnexedParamSectionForm,
  AnnexedParamSectionTableModel
} from '../../../../components/shared/annexedParamAccordionSection/annexedParamAccordionSection.model'
import { enumToSelectOptionArray, enumToSelectOptionArrayDefaultValues } from '../../../../helpers'
import { getFilteredBreakdownOptions } from '../../../../helpers/breakdownOptionsHelper'
import { useFormDialog } from '../../../../hooks'
import { useDropdownOptions } from '../../../../hooks/useDropdownOptions'
import { getCustomerDomain } from '../../../../redux'
import { getAnnexedParamsByType } from '../../../../services/annexedParams.service'
import { fetchTolls } from '../../../contracts/contracts.service'
import {
  fetchProductById,
  fetchProductsBySubjectType,
  fetchWeightedTypeListByBreakdownType
} from '../../../products/products.service'
import { profileModelToSelectOptions } from '../../../profiles/profiles.mapper'
import { fetchProfilesByLeadType } from '../../../profiles/profiles.services'
import { mapTollsResponseToSelectOption } from '../../leads.mapper'

export interface LeadFormProps {
  leadFormData: LeadFormModel
  annexedParamsData?: AnnexedParamSectionTableModel[]
  onFormChange: (formState: LeadFormModel, formIsCompleted: boolean) => void
  onAnnexedParamsChange: (annexedParamsDataTable: AnnexedParamSectionTableModel[]) => void
  commodity: EnumCommodity
  leadType: EnumTipoLead
}

export interface LeadFormModel {
  id?: number
  commodity: EnumCommodity
  leadType: EnumTipoLead
  leadState?: EnumEstadoLead
  breakdownLevel?: EnumNivelDesglose
  weightedType?: EnumProrrateo
  profileId?: number
  code: string
  description?: string
  commentaries?: string
  productId?: number // if leadType is Precios productId is optional
  tollId?: number //  if leadType is Precios tollId is optional
  supplyPointId?: number
}

interface LeadFormValidation {
  commodity: boolean
  leadType: boolean
  code: boolean
  productId: boolean
  tollId: boolean
  profileId: boolean
  breakdownLevel: boolean
  weightedType: boolean
  allComplete: boolean
}

const initialFormValidation = {
  commodity: false,
  leadType: false,
  code: false,
  productId: false, //if leadType is Precios, productId is optional
  tollId: false, //if leadType is Precios, tollId is optional
  allComplete: false
} // used in handleFormIsEnabled

export const LeadForm: React.FC<LeadFormProps> = ({
  leadFormData,
  annexedParamsData,
  onFormChange,
  onAnnexedParamsChange,
  commodity,
  leadType
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'leads', 'enums'])
  const { info, error } = useNotify()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog } = useFormDialog()

  const [formData, setFormData] = useState<LeadFormModel>(leadFormData ?? ({} as LeadFormModel))
  const [annexedParams, setAnnexedParams] = useState<AnnexedParamSectionTableModel[]>(
    annexedParamsData ?? []
  )

  const leadTypeIsPrecios = leadType === EnumTipoLead.Precios

  const [formValidations, setFormValidations] = useState<LeadFormValidation>({
    ...initialFormValidation,
    commodity: !!commodity,
    leadType: !!leadType,
    code: !!leadFormData.code,
    weightedType: !!leadFormData.weightedType,
    breakdownLevel: !!leadFormData.breakdownLevel,
    profileId: !!leadFormData.profileId,
    productId: leadTypeIsPrecios ? true : !!leadFormData.productId, //if leadType is Precios, productId is optional
    tollId: leadTypeIsPrecios ? true : !!leadFormData.tollId, //if leadType is Precios, tollId is optional
    allComplete: Object.keys(leadFormData).length === 0 ? false : true
  }) // validations must be true if form is on EditMode
  const [productOptions, setProductOptions] = useState<ProductSelectOption[]>([])
  const [tollOptions, setTollOptions] = useState<SelectOption[]>([])
  const [annexedParamsOptions, setAnnexedParamsOptions] = useState<AnnexedParamsModel[]>([])
  const [breakdownOptions, setBreakdownOptions] = useState<SelectOption[] | undefined>([])

  const [isFetchingProducts, setIsFetchingProducts] = useState<boolean>(false)
  const [isFetchingTolls, setIsFetchingTolls] = useState<boolean>(false)
  const [isFetchingAnnexedParams, setIsFetchingAnnexedParams] = useState<boolean>(false)
  const weightedTypeDropdownOption = useDropdownOptions<EnumNivelDesglose, [number]>(
    fetchWeightedTypeListByBreakdownType
  )
  const profileDropdownOption = useDropdownOptions<ProfileModel, [number, number]>(
    fetchProfilesByLeadType
  )

  // #region Handlers Starts

  // ------------------- AnnexedParams DataTable Handlers -----------------------------//
  const handleAddAnnexedLead = (formData: AnnexedParamSectionForm) => {
    setAnnexedParams(prevState => {
      const newAnnexedParams = [...prevState, mapAnnexedParamFormDataToAnnexedParamTable(formData)]
      onAnnexedParamsChange(newAnnexedParams)
      return newAnnexedParams
    })
  }
  const handleEditAnnexedLead = (formData: AnnexedParamSectionForm) => {
    setAnnexedParams(prevState => {
      const newAnnexedParams = annexedParams.map(param => {
        if (param.annexedParam === formData.annexedParamId) {
          return mapAnnexedParamFormDataToAnnexedParamTable(formData)
        }
        return param
      })
      onAnnexedParamsChange(newAnnexedParams)
      return newAnnexedParams
    })
  }
  const handleDeleteAnnexedLead = (selectedRow: AnnexedParamSectionTableModel) => {
    setAnnexedParams(prevState => {
      const newAnnexedParams = prevState.filter(
        param => param.annexedParam !== selectedRow.annexedParam
      )
      onAnnexedParamsChange(newAnnexedParams)
      return newAnnexedParams
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const handleFormIsEnabled = (key: keyof LeadFormValidation, value: boolean): boolean => {
    const formIsCompleted = Object.keys(initialFormValidation)
      .filter(k => k !== 'allComplete')
      .filter(k => !inputIsOptional(k as keyof LeadFormValidation))
      .every(k => (k === key ? value : formValidations[k as keyof LeadFormValidation]))

    setFormValidations(prevState => ({
      ...prevState,
      [key]: value,
      allComplete: formIsCompleted
    }))

    return formIsCompleted
  }

  const inputIsOptional = (key: keyof LeadFormValidation): boolean => {
    if (key === 'productId') {
      return leadTypeIsPrecios
    }
    if (key === 'tollId') {
      return leadTypeIsPrecios
    }
    return false
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const options: FormOptions = {
    elevation: 0,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('leads:forms.add.analytics')
    }
  }

  const addFormSections: Section[] = [
    {
      title: t('leads:forms.add.section1Title'),
      inputs: [
        {
          name: 'id',
          type: DataType.TEXT,
          display: false
        },
        {
          name: 'code',
          label: t('leads:forms.add.inputs.code'),
          type: DataType.TEXT,
          required: true,
          onChange: (value, formState) => {
            const isComplete = !!value && value !== '' // change value from string|number to boolean
            const formIsCompleted = handleFormIsEnabled('code', isComplete)
            const castFormState = formState as LeadFormModel
            onFormChange(castFormState, formIsCompleted)
          }
        },
        {
          name: 'description',
          label: t('leads:forms.add.inputs.description'),
          type: DataType.TEXT
        },
        {
          name: 'leadType',
          label: t('leads:forms.add.inputs.leadType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTipoLead, 'enums:EnumTipoLead'),
          readOnly: true,
          defaultValue: leadType
        },
        {
          name: 'commodity',
          label: t('leads:forms.add.inputs.commodity'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
          readOnly: true,
          defaultValue: commodity
        },
        {
          name: 'leadState',
          label: t('leads:forms.add.inputs.leadState'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumEstadoLead, 'enums:EnumEstadoLead'),
          readOnly: true,
          defaultValue: formData.leadState
        },
        {
          name: 'commentaries',
          label: t('leads:forms.add.inputs.commentaries'),
          type: DataType.TEXT,
          inputSize: 9,
          onChange: (value, formState) => {
            const castFormState = formState as LeadFormModel
            onFormChange(castFormState, formValidations.allComplete)
          }
        }
      ]
    },
    {
      title: t('leads:forms.add.section2Title'),
      // collapsible: true,
      inputs: [
        {
          name: 'productId',
          label: t('leads:forms.add.inputs.product'),
          isLoading: isFetchingProducts,
          type: DataType.SELECT,
          selectOptions: productOptions,
          required: !leadTypeIsPrecios,
          clearable: leadTypeIsPrecios,
          onChange: async (value, formState) => {
            const product = await fetchProductById(+value)
            const isComplete = inputIsOptional('productId') || (!!value && value !== '') // change value from string|number to boolean
            const formIsCompleted = handleFormIsEnabled('productId', isComplete)
            const castFormState = formState as LeadFormModel
            const productBreakdown = (product.data && product.data.breakdownType) || undefined
            const productWeightedType = (product.data && product.data.weightedType) || undefined
            const newFormState: LeadFormModel = {
              ...castFormState,
              breakdownLevel: productBreakdown,
              weightedType: productWeightedType,
              profileId: undefined
            }

            setFormData(newFormState)
            onFormChange(newFormState, formIsCompleted)

            if (productBreakdown !== undefined) {
              weightedTypeDropdownOption.fetchData(productBreakdown)
              profileDropdownOption.fetchData(commodity, productBreakdown)
            }
          }
        },
        {
          name: 'tollId',
          label: t('leads:forms.add.inputs.toll'),
          isLoading: isFetchingTolls,
          type: DataType.SELECT,
          selectOptions: mapTollsResponseToSelectOption(tollOptions),
          required: !leadTypeIsPrecios,
          clearable: leadTypeIsPrecios,
          onChange: (value, formState) => {
            const isComplete = inputIsOptional('tollId') || (!!value && value !== '') // change value from string|number to boolean
            const formIsCompleted = handleFormIsEnabled('tollId', isComplete)
            const castFormState = formState as LeadFormModel
            onFormChange(castFormState, formIsCompleted)
          }
        },
        {
          name: 'breakdownLevel',
          label: t('leads:components.profileQuantityCard.form.inputs.breakdownLevel'),
          type: DataType.SELECT,
          selectOptions: breakdownOptions,
          inputSize: 3,
          defaultValue: formData.breakdownLevel,
          required: true,
          onChange: (value: number | string, formState: any) => {
            const breakdownTypeId = +value
            const isComplete = inputIsOptional('breakdownLevel') || (!!value && value !== '') // change value from string|number to boolean
            const formIsCompleted = handleFormIsEnabled('breakdownLevel', isComplete)
            const newFormState = {
              ...formState,
              breakdownLevel: breakdownTypeId,
              weightedType: undefined,
              profileId: undefined
            }
            setFormData(newFormState)
            onFormChange(newFormState, formIsCompleted)
            weightedTypeDropdownOption.fetchData(breakdownTypeId)
            profileDropdownOption.fetchData(commodity, breakdownTypeId)
          }
        },
        {
          name: 'weightedType',
          label: t('leads:components.profileQuantityCard.form.inputs.weightedType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArrayDefaultValues(
            EnumProrrateo,
            'enums:EnumProrrateo',
            weightedTypeDropdownOption.responseData
          ),
          isLoading: weightedTypeDropdownOption.isLoading,
          inputSize: 3,
          required: true,
          onChange: (value: number | string, formState: any) => {
            const isComplete = inputIsOptional('weightedType') || (!!value && value !== '') // change value from string|number to boolean
            const formIsCompleted = handleFormIsEnabled('weightedType', isComplete)
            const newFormState = { ...formState, weightedType: +value }
            setFormData(newFormState)
            onFormChange(newFormState, formIsCompleted)
          }
        },
        {
          name: 'profileId',
          label: t('leads:components.profileQuantityCard.form.inputs.profile'),
          type: DataType.SELECT,
          selectOptions: profileModelToSelectOptions(profileDropdownOption.responseData),
          isLoading: profileDropdownOption.isLoading,
          inputSize: 3,
          required: false,
          onChange: (value: number | string, formState: any) => {
            const isComplete = inputIsOptional('profileId') || (!!value && value !== '') // change value from string|number to boolean
            const formIsCompleted = handleFormIsEnabled('profileId', isComplete)
            const newFormState = { ...formState, profileId: +value }
            setFormData(newFormState)
            onFormChange(newFormState, formIsCompleted)
          }
        }
      ]
    }
  ]
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  useEffect(() => {
    const getTollOptions = async () => {
      setIsFetchingTolls(true)
      await fetchTolls(commodity!)
        .then(response => {
          if (response.success && response.data) {
            setTollOptions(response.data)
          } else {
            error(t('leads:fetchErrors.tolls'))
          }
        })
        .finally(() => {
          setIsFetchingTolls(false)
        })
    }
    const getProductOptions = async () => {
      setIsFetchingProducts(true)
      await fetchProductsBySubjectType(EnumTipoSujeto.ClienteFinal, commodity)
        .then(response => {
          if (response.success && response.data) {
            setProductOptions(response.data)
          } else {
            error(t('leads:fetchErrors.products'))
          }
        })
        .finally(() => {
          setIsFetchingProducts(false)
        })
    }
    const fetchAnnexedParamsByType = async () => {
      setIsFetchingAnnexedParams(true)
      await getAnnexedParamsByType(EnumTipoParamAnexo.Lead, commodity)
        .then(response => {
          if (response.success && response.data) {
            setAnnexedParamsOptions(response.data)
          } else {
            error(t('leads:fetchErrors.annexedParams'))
          }
        })
        .finally(() => {
          setIsFetchingAnnexedParams(false)
        })
    }

    fetchAnnexedParamsByType()
    getTollOptions()
    getProductOptions()
  }, [commodity])

  useEffect(() => {
    const filtered = getFilteredBreakdownOptions(commodity)
    setBreakdownOptions(filtered)
    if (
      formData.breakdownLevel !== undefined &&
      !filtered?.some(o => o.value === formData.breakdownLevel)
    ) {
      const newFormData: LeadFormModel = {
        ...formData,
        breakdownLevel: undefined,
        weightedType: undefined,
        profileId: undefined
      }
      setFormData(newFormData)
      onFormChange(newFormData, handleFormIsEnabled('breakdownLevel', false))
    }
  }, [commodity])

  useEffect(() => {
    if (formData && formData.breakdownLevel !== undefined) {
      weightedTypeDropdownOption.fetchData(formData.breakdownLevel)
      profileDropdownOption.fetchData(commodity, formData.breakdownLevel)
    }
  }, [formData.breakdownLevel])
  // #endregion Hooks Ends

  return (
    <>
      <Form sections={addFormSections} data={formData} options={options} />
      <AnnexedParamAccordionSection
        defaultExpanded={true}
        title={t('leads:forms.add.section3Title')}
        isLoading={isFetchingAnnexedParams}
        withDescriptions={true}
        tableData={annexedParams}
        annexedParamsOptions={annexedParamsOptions}
        onRowAdd={handleAddAnnexedLead}
        onRowEdit={handleEditAnnexedLead}
        onRowDelete={handleDeleteAnnexedLead}
      />
      <CustomFormDialog {...formProps} />
    </>
  )
}
